import React, { useEffect, useState } from 'react';
import { 
    SimpleForm, 
    TextInput, 
    NumberInput, 
    PasswordInput, 
    SelectInput, 
    SelectArrayInput, 
    required, 
    useDataProvider, 
    maxLength, 
    email, 
    regex,
    Toolbar,
    SaveButton,
    useGetIdentity,
    useNotify,
    useRefresh,
    useRecordContext,
} from 'react-admin';
import { useWatch } from 'react-hook-form';
import { Grid, Typography, Button } from '@mui/material';
import { ICity, roleChoices } from '../types';
import { mapStatesToChoices, resolveStateCode, StateChoice } from '../utils/stateUtils'; 
import { useParams, useNavigate } from 'react-router-dom';
import { UserRole } from '../types/types';
import BlockIcon from '@mui/icons-material/Block';
import RestoreIcon from '@mui/icons-material/Restore';
import { SellerAdminFields } from './SellerAdminFields';
import { GstStateChoice, SellerGstManager } from './SellerGstManager';

const CustomToolbar = () => {
    const { identity } = useGetIdentity();
    const userRole = identity?.role_id;
    const params = useParams();
    const userId = params.id;
    const dataProvider = useDataProvider();
    const notify = useNotify();
    const refresh = useRefresh();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);
    const [active, setActive] = useState<boolean | undefined>(undefined);

    useEffect(() => {
        if (userId && userRole === UserRole.Admin) {
            dataProvider.getOne('users', { id: userId })
                .then(({ data }) => setActive(data?.active !== false))
                .catch(() => setActive(undefined));
        }
    }, [userId, userRole, dataProvider]);

    const handleMakeInactive = async () => {
        if (!userId || !window.confirm('The user would be made inactive and would not be able to log in and make updates to the prices. Are you sure you want to make the user in seller inactive?')) return;
        setLoading(true);
        try {
            await dataProvider.delete('users', { id: userId });
            notify('User made inactive successfully', { type: 'info' });
            navigate('/users');
        } catch (e) {
            notify('Failed to make user inactive', { type: 'error' });
        } finally {
            setLoading(false);
        }
    };

    const handleReactivate = async () => {
        if (!userId || !window.confirm('Are you sure you want to reactivate this seller? They will be able to log in and make updates again.')) return;
        setLoading(true);
        try {
            const baseUrl = process.env.REACT_APP_API_URL || '';
            const token = localStorage.getItem('user_authToken');
            const res = await fetch(`${baseUrl}/users/${userId}/active`, {
                method: 'PATCH',
                headers: {
                    'Content-Type': 'application/json',
                    ...(token ? { Authorization: `Bearer ${token}` } : {}),
                },
                body: JSON.stringify({ active: true }),
            });
            if (!res.ok) throw new Error('Failed');
            notify('User reactivated successfully', { type: 'info' });
            setActive(true);
            refresh();
        } catch (e) {
            notify('Failed to reactivate user', { type: 'error' });
        } finally {
            setLoading(false);
        }
    };

    return (
        <Toolbar>
            <SaveButton />
            {userRole === UserRole.Admin && active === true && (
                <Button color="error" startIcon={<BlockIcon />} onClick={handleMakeInactive} disabled={loading}>
                    Make Inactive
                </Button>
            )}
            {userRole === UserRole.Admin && active === false && (
                <Button color="primary" startIcon={<RestoreIcon />} onClick={handleReactivate} disabled={loading}>
                    Reactivate
                </Button>
            )}
        </Toolbar>
    );
};

interface UserFormProps {
    isEdit: boolean;
}

type UserWarehousesInputProps = {
    cities: { id: number; name: string }[];
    userCities: number[];
};

type SellerGstFieldsProps = {
    states: GstStateChoice[];
};

const SellerGstFields: React.FC<SellerGstFieldsProps> = ({ states }) => {
    const record = useRecordContext();
    const roleId = useWatch({ name: 'role_id' }) ?? record?.role_id;
    const isSeller = Number(roleId) === UserRole.Seller;
    const validateGST = regex(/^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/, 'Invalid GST number');
    const validateGSTYear = regex(/^[0-9]{4}$/, 'Invalid year format');

    if (isSeller) {
        return (
            <Grid item xs={12}>
                <SellerGstManager states={states} />
            </Grid>
        );
    }

    return (
        <>
            <Grid item xs={6} sm={3}>
                <TextInput source="gst_number" label="GST Number" fullWidth validate={validateGST} />
            </Grid>
            <Grid item xs={6} sm={3}>
                <NumberInput source="gst_registration_year" label="GST Registration Year" fullWidth validate={validateGSTYear} />
            </Grid>
        </>
    );
};

/** Warehouses required for most sellers; optional for marketplace (polymer market) sellers. */
const UserWarehousesInput: React.FC<UserWarehousesInputProps> = ({ cities, userCities }) => {
    const record = useRecordContext();
    const roleId = useWatch({ name: 'role_id' }) ?? record?.role_id;
    const sellerCategory = useWatch({ name: 'seller_category' }) ?? record?.seller_category;
    const isMarketplaceSeller =
        Number(roleId) === UserRole.Seller && sellerCategory === 'marketplace';

    return (
        <Grid item xs={12}>
            <SelectArrayInput
                label="Warehouses"
                source="cities"
                choices={cities}
                defaultValue={userCities}
                optionText="name"
                optionValue="id"
                translateChoice={false}
                fullWidth
                helperText={
                    isMarketplaceSeller ? 'Optional for marketplace sellers.' : undefined
                }
                validate={isMarketplaceSeller ? [maxLength(30)] : [required(), maxLength(30)]}
            />
        </Grid>
    );
};

export const UserForm: React.FC<UserFormProps> = ({ isEdit }) => {
    const [isLoading, setIsLoading] = useState(true); 

    const dataProvider = useDataProvider();
    const [cities, setCities] = useState<{ id: number; name: string }[]>([]);
    const [userCities, setUserCities] = useState<number[]>([]);
    const validatePassword = isEdit ? undefined : required();
    const [gstStates, setGstStates] = useState<GstStateChoice[]>([]);
    const validatePin = regex(/^[0-9]{6}$/, 'Invalid PIN');
    const validatePhoneLandline = regex(/^[0-9-]+$/, 'Invalid landline number');
    const validatePhoneMobile = regex(/^[0-9]{10}$/, 'Invalid mobile number');
    const validateUsername = [
        required(),
        regex(/^[A-Za-z0-9 ]{1,10}$/, 'Invalid username (letters, numbers, spaces; max 10 characters)'),
        maxLength(10),
    ];
    const params = useParams();
    const userId = params.id;
    const [states, setStates] = useState<StateChoice[]>([]); 
    const role_id = localStorage.getItem('user_role_id');
    const userRole = role_id ? parseInt(role_id) as UserRole : null; 

    useEffect(() => {
        setIsLoading(true);
        const fetchCities = async () => {
            try {
                const citiesResponse = await dataProvider.getList('statecities/withstates', {
                    pagination: { page: 1, perPage: 1000 },
                    sort: { field: 'city_name', order: 'ASC' },
                    filter: {}
                });
                setCities(citiesResponse.data.map((city: ICity) => ({ id: city.id, name: city.city_name })));
                if (isEdit && userId) {
                    const userCitiesResponse = await dataProvider.getList(`users/getusercities/${userId}`, {
                        pagination: { page: 1, perPage: 1000 },
                        sort: { field: 'city_name', order: 'ASC' },
                        filter: {}
                    });
                    setUserCities(userCitiesResponse.data.map((city: ICity) => city.id));
                }
                const statesResponse = await dataProvider.getList('states', {
                    pagination: { page: 1, perPage: 100 },
                    sort: { field: 'state_name', order: 'ASC' },
                    filter: {}
                });
                setStates(mapStatesToChoices(statesResponse.data));
                setGstStates(
                    (statesResponse.data || []).map((state: { id: number; state_name?: string; name?: string; state_code?: string; tin_number?: string }) => ({
                        id: state.id,
                        name: state.state_name || state.name || '',
                        state_code: state.state_code || '',
                        tin_number: String(state.tin_number || ''),
                    })).filter((state: GstStateChoice) => Boolean(state.name) && Boolean(state.state_code))
                );

                setIsLoading(false);
            } catch (error) {
                console.error(error);
                setIsLoading(false);
            }
        };

        fetchCities();
    }, [userId, isEdit, dataProvider]);

    if (isLoading) {
        return <div>Loading...</div>;
    }
  
    return (
        <SimpleForm toolbar={<CustomToolbar />}>
            <Grid container spacing={2}>
                {/* Rest of the form content remains the same */}
                {/* Business Info */}
                <Grid item xs={12}>
                    <Typography variant="h6">Business Info</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                    <TextInput source="company_name" label="Company Name" fullWidth />
                </Grid>
                <SellerGstFields states={gstStates} />

                {/* Personal Info */}
                <Grid item xs={6} sm={6}>
                    <TextInput source="user_first_name" label="First Name" fullWidth validate={[required(), maxLength(30)]}/>
                </Grid>
                <Grid item xs={6} sm={6}>
                    <TextInput source="user_last_name" label="Last Name" fullWidth validate={[maxLength(30)]}/>
                </Grid>
                <Grid item xs={12}>
                    <TextInput source="email_address" label="Email Address" fullWidth validate={[email()]}/>
                </Grid>

                {/* Address */}
                <Grid item xs={12} sm={6}>
                    <TextInput source="address_line1" label="Address Line 1" fullWidth validate={[maxLength(50)]}/>
                </Grid>
                <Grid item xs={12} sm={6}>
                    <TextInput source="address_line2" label="Address Line 2" fullWidth validate={[maxLength(50)]}/>
                </Grid>
                <Grid item xs={4}>
                    <TextInput source="address_city" label="City" fullWidth validate={[maxLength(30)]}/>
                </Grid>
                <Grid item xs={4}>
                    <SelectInput
                        source="address_state"
                        label="State"
                        choices={states}
                        optionText="name"
                        optionValue="state_code"
                        format={(value) => resolveStateCode(value, states)}
                        fullWidth
                    />
                </Grid>
                <Grid item xs={4}>
                    <TextInput source="address_pin" label="PIN" fullWidth validate={validatePin}/>
                </Grid>

                {/* Contact */}
                <Grid item xs={6}>
                    <TextInput source="phone_landline" label="Landline Phone" fullWidth validate={validatePhoneLandline}/>
                </Grid>
                <Grid item xs={6}>
                    <TextInput source="phone_mobile" label="Mobile Phone" fullWidth validate={validatePhoneMobile}/>
                </Grid>

                {/* Delivery Info */}
                <Grid item xs={12}>
                    <Typography variant="h6">Delivery Info</Typography>
                </Grid>
                <Grid item xs={12}>
                    <TextInput source="delivery_contact_person" label="Delivery Contact Person" fullWidth validate={[maxLength(50)]}/>
                </Grid>
                <Grid item xs={12} sm={6}>
                    <TextInput source="delivery_address_line1" label="Delivery Address Line 1" fullWidth validate={[maxLength(50)]}/>
                </Grid>
                <Grid item xs={12} sm={6}>
                    <TextInput source="delivery_address_line2" label="Delivery Address Line 2" fullWidth validate={[maxLength(50)]}/>
                </Grid>
                <Grid item xs={4}>
                    <TextInput source="delivery_address_city" label="Delivery City" fullWidth validate={[maxLength(30)]}/>
                </Grid>
                <Grid item xs={4}>
                    <SelectInput
                        source="delivery_address_state"
                        label="Delivery State"
                        choices={states}
                        optionText="name"
                        optionValue="state_code"
                        format={(value) => resolveStateCode(value, states)}
                        fullWidth
                    />
                </Grid>
                <Grid item xs={4}>
                    <TextInput source="delivery_address_pin" label="Delivery PIN" fullWidth validate={validatePin}/>
                </Grid>

                {/* Additional Info */}
                <Grid item xs={12}>
                    <TextInput source="payment_terms" label="Payment Terms" fullWidth validate={[maxLength(50)]}/>
                </Grid>

                {/* Profile Info */}
                <Grid item xs={12}>
                    <Typography variant="h6">Profile Info</Typography>
                </Grid>
                <Grid item xs={12}>
                    <TextInput source="user_name" label="User Name" fullWidth validate={validateUsername}/>
                </Grid>
                <Grid item xs={12}>
                    <PasswordInput 
                        source="user_password" 
                        label="Password" 
                        fullWidth 
                        validate={validatePassword}
                        format={(value) => {
                            // Convert empty string to undefined to exclude from form submission
                            const result = value || undefined;
                            // Log password field state (without exposing actual password)
                            if (isEdit) {
                                console.log('🔐 [FRONTEND] Password field in edit mode - value present:', result !== undefined, 'length:', result ? result.length : 0);
                            }
                            return result;
                        }}
                        parse={(value) => {
                            // Ensure undefined for empty values to prevent sending empty string
                            const result = value || undefined;
                            // Log what will be sent to backend
                            if (isEdit && result !== undefined) {
                                console.log('🔐 [FRONTEND] Password will be sent to backend - length:', result.length);
                            } else if (isEdit) {
                                console.log('🔐 [FRONTEND] Password field empty - will be excluded from submission');
                            }
                            return result;
                        }}
                    />
                </Grid>
                {userRole === UserRole.Admin && (     
                    <>       
                    <Grid item xs={12}>
                        <SelectInput 
                            source="role_id" 
                            label="Role" 
                            choices={roleChoices} 
                            optionText="name" 
                            optionValue="id" 
                            validate={required()}
                            fullWidth 
                        />
                    </Grid>

                    {isEdit && userId && (
                        <SellerAdminFields userId={userId} />
                    )}

                    <UserWarehousesInput cities={cities} userCities={userCities} />
                    </>
                )}
                {isEdit &&
                    userId &&
                    userRole === UserRole.ProductAdmin && (
                        <SellerAdminFields userId={userId} sellerTypeOnly />
                    )}
            </Grid>
        </SimpleForm>
    );
};